import { flatten } from "lodash-es";

import { joinPaths } from "../navigation/utilities.js";
import { mergeParsers, type Parser } from "../parsing/index.js";
import Endpoint, {
    type AnyEndpoint,
    type ContextBase,
    type PathParamsBase,
    type QueryParamsBase,
} from "./Endpoint.js";

export default class EndpointRouter<
    PathParams extends PathParamsBase,
    Context extends ContextBase,
> {
    contextParser: Parser<Context> | null = null;
    path: string | null = null;
    pathParamsParser: Parser<PathParams> | null = null;

    // Path

    setPath<NewPathParams extends PathParamsBase>(
        newOwnPath: string,
        parser: Parser<NewPathParams>,
    ) {
        const router = new EndpointRouter<NewPathParams, Context>();
        router.path = joinPaths(this.path, newOwnPath);
        router.pathParamsParser = parser;
        router.contextParser = this.contextParser;
        router.parent = this.parent;
        router.routerChildren = this.routerChildren;
        router.endpointChildren = this.endpointChildren;
        return router;
    }

    getPath(): string {
        return this.path || "";
    }

    // Context

    setContextParser<NewContext extends ContextBase>(
        newContext: Parser<NewContext>,
    ): EndpointRouter<PathParams, NewContext & Context> {
        const router = new EndpointRouter<PathParams, NewContext & Context>();
        router.path = this.path;
        router.pathParamsParser = this.pathParamsParser;
        router.contextParser = mergeParsers(this.contextParser, newContext);
        router.parent = this.parent;
        router.routerChildren = this.routerChildren;
        router.endpointChildren = this.endpointChildren;
        return router;
    }

    // Parent

    parent: AnyEndpointRouter | null = null;

    setParent<ParentPathParams extends PathParamsBase, ParentContext extends ContextBase>(
        parent: EndpointRouter<ParentPathParams, ParentContext>,
    ): EndpointRouter<PathParams & ParentPathParams, Context & ParentContext> {
        const router = new EndpointRouter<PathParams & ParentPathParams, Context & ParentContext>();
        router.path = joinPaths(parent.path, this.path);
        router.pathParamsParser = mergeParsers(this.pathParamsParser, parent.pathParamsParser);
        router.contextParser = mergeParsers(this.contextParser, parent.contextParser);
        router.parent = parent;
        router.routerChildren = this.routerChildren;
        router.endpointChildren = this.endpointChildren;
        return router;
    }

    getParents(): Array<AnyEndpointRouter> {
        const output: Array<AnyEndpointRouter> = [];
        if (this.parent) output.push(...this.parent.getParents(), this.parent);
        return output;
    }

    // Children

    routerChildren: Array<AnyEndpointRouter> = [];

    addRouter<ChildPathParams extends PathParamsBase, ChildContext extends ContextBase>(
        router: EndpointRouter<ChildPathParams, ChildContext>,
    ): EndpointRouter<PathParams & ChildPathParams, Context & ChildContext> {
        const newRouter = router.setParent(this);
        this.routerChildren.push(newRouter);
        return newRouter;
    }

    endpointChildren: Array<AnyEndpoint> = [];

    addEndpoint<
        ChildPathParams extends PathParamsBase,
        ChildQueryParams extends QueryParamsBase,
        ChildBody,
        ChildContext extends ContextBase,
        ChildResult,
    >(endpoint: Endpoint<ChildPathParams, ChildQueryParams, ChildBody, ChildContext, ChildResult>) {
        const newEndpoint = endpoint.setRouter(this);
        this.endpointChildren.push(newEndpoint);
        return newEndpoint;
    }

    getEndpoints(): Array<AnyEndpoint> {
        const endpoints: Array<AnyEndpoint> = [];
        this.routerChildren.forEach((r) => endpoints.push(...r.getEndpoints()));
        endpoints.push(...this.endpointChildren);
        return endpoints;
    }

    getChildrenRouters(): Array<AnyEndpointRouter> {
        const output: Array<AnyEndpointRouter> = [];
        output.push(...flatten(this.routerChildren.map((r) => r.getChildrenRouters())));
        output.push(this);
        return output;
    }
}

export type AnyEndpointRouter = EndpointRouter<any, any>;
